import React, { useRef, useMemo } from 'react';
import { theme } from '../../../theme';
import type { PeriodResidueBin, PrimaryAxisVolumeBin } from '../../../serverTypes';
import { useEChartsLifecycle, STD_TOOLTIP_STYLE } from './_chartCommon';
import { useCategoryMeta } from '../../../context/ClassificationSchemaContext';
import { residueByBinLabel, hasResidue, barLabelText, RESIDUE_LABEL_RICH, withResidueDays, residueTooltipLines } from './_volumeStack';

// <purpose>Tag.Balken — lollipop chart: 1.5px stems + scatter cap dots. Caps fill on hover.</purpose>
// <constraints>
//   part 7 round 5, 2026-10-14 (operator-accepted screen): the stem is the CLASSIFIED total of the day, read from
//   the daily bins (rawData = perPrimaryAxisVolumeByPeriod, binLabel dd.mm.yyyy) minus the unclassified value —
//   the same class the KW/Quartal bars plot. The x axis stays dailyVolume's days, so a day whose mails all failed
//   still stands (at 0). A day with residue (stats.perPeriodResidue, matched by binLabel) carries a cap label:
//   the classified total, then „+f +w" (red · grey); other days no label (as before). The tooltip names the
//   residue words with their counts. Absent residue list ⇒ no „+n", no words; absent bins ⇒ the served day total.
// </constraints>

type Props = {
  /** One entry per day of stats.dailyVolume; binLabel = the day in the bins' grammar (dd.mm.yyyy). */
  data: Array<{ date: string; count: number; binLabel?: string }>;
  rawData?: PrimaryAxisVolumeBin[];
  residue?: PeriodResidueBin[];
  filterHash?: string;
};

export const TagBalken: React.FC<Props> = ({ data: servedData, rawData, residue, filterHash }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // The axis: classified bins UNION residue bins (a residue-only period stands at 0 with its „+n").
  const data = useMemo(() => withResidueDays(servedData, residue), [servedData, residue]);
  const cm = useCategoryMeta();
  const NAVY = theme.colors.navy;
  const NAVY_MID = theme.colors.navyMid;

  const classifiedPerDay = useMemo(() => {
    if (!rawData) return undefined;
    const map = new Map<string, number>();
    for (const b of rawData) {
      if (cm.isUnclassified(b.primaryAxis)) continue;
      map.set(b.binLabel, (map.get(b.binLabel) ?? 0) + b.count);
    }
    return map;
  }, [rawData, cm]);
  const residueMap = useMemo(() => residueByBinLabel(residue), [residue]);

  useEChartsLifecycle(containerRef, chart => {
    const values = data.map(d => classifiedPerDay ? (classifiedPerDay.get(d.binLabel ?? '') ?? 0) : d.count);
    const labels = data.map(d => d.date);
    const residues = data.map(d => residueMap?.get(d.binLabel ?? ''));

    chart.setOption({
      grid: { left: 36, right: 16, top: 38, bottom: 26 }, // top room for a two-line residue label (as KwBalken)
      tooltip: {
        ...STD_TOOLTIP_STYLE,
        trigger: 'axis',
        axisPointer: { type: 'line', lineStyle: { color: NAVY, opacity: 0.35, width: 1 } },
        // prefer cap series value; fallback to first
        formatter: (params: any) => {
          const arr = Array.isArray(params) ? params : [params];
          const p = arr.find((x: any) => x.seriesName === 'cap') ?? arr[0];
          if (!p) return '';
          const residueLines = residueTooltipLines(residues[p.dataIndex], cm);
          return `<div style="font-size:10px;color:#888">${p.axisValue}</div><div style="font-size:14px;color:${NAVY};font-weight:600">${p.value} <span style="font-size:10px;font-weight:400;color:#888">Tickets</span></div>${residueLines}`;
        },
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: '#dde0e8' } },
        axisTick: { show: false },
        axisLabel: { color: '#9095a3', fontSize: 8, interval: Math.max(0, Math.floor(data.length / 8) - 1) },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#f1f2f6' } },
        axisLabel: { color: '#9095a3', fontSize: 9 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          name: 'stem',
          type: 'bar',
          data: values,
          barWidth: 2,
          itemStyle: { color: NAVY_MID, opacity: 0.7 },
          emphasis: { itemStyle: { opacity: 1 } },
          z: 2,
        },
        {
          name: 'cap',
          type: 'scatter',
          data: values,
          symbolSize: 8,
          itemStyle: { color: 'white', borderColor: NAVY, borderWidth: 1.5 },
          label: {
            show: true,
            position: 'top',
            color: NAVY,
            fontSize: 10,
            fontWeight: 600,
            formatter: (p: any) => hasResidue(residues[p.dataIndex]) ? barLabelText(values[p.dataIndex], residues[p.dataIndex]) : '',
            rich: RESIDUE_LABEL_RICH,
          },
          emphasis: {
            itemStyle: { color: NAVY, borderColor: 'white', borderWidth: 1.5, shadowColor: 'rgba(6,16,90,0.45)', shadowBlur: 10 },
            scale: 1.4,
          },
          z: 3,
        },
      ],
    });
  }, [data, classifiedPerDay, residueMap, filterHash, cm]);

  return <div ref={containerRef} style={{ width: '100%', height: 220 }} />;
};
